import { Component, inject } from '@angular/core';
import { OEP_ENTITY, OEP_LIVE_FILTER } from 'agu_oep_core';
import { OEPViewBase } from '../../state/oep-view-base';
import { OEPDataService } from '../../data/oep-data.service';
import { OEPFilesService } from '../../data/oep-files.service';
import type { OEPStatusLine } from '../shared/status';
import { OEPLatestDrafts, OEPPresentationLinkSql, OEPUploadedFilesSql } from '../shared/files';
import { OEP_TAB_BLOCKED, OEPOpenInNewTab } from '../shared/open-tab';
import { AsReviewStatus, BuildReviewsVM, type OEPAssignmentInput, type OEPAssignmentRowVM, type ReviewsVM } from './reviews.vm';
import { DateOrNull, Str, StrOrNull, Up } from '../shared/rows';

/** My live assignments this season (same predicate as OEPContextService's reviewer counts). */
export function OEPMyAssignmentsSql(me: string, seasonID: string): string {
    return `SELECT ID FROM FeedbackPlatform.ReviewAssignment WHERE ReviewerUserID='${OEPDataService.G(me)}' AND ${OEP_LIVE_FILTER.Assignment} AND PresentationID IN (SELECT ID FROM FeedbackPlatform.Presentation WHERE SeasonID='${OEPDataService.G(seasonID)}' AND ${OEP_LIVE_FILTER.Presentation})`;
}

/**
 * Reviewer list ("Review draft presentations") — markup.html:493-554. All copy and rules live in
 * reviews.vm.ts (pure, spec'd); this gathers ReviewsInputs in ONE RunViews batch
 * (OEPDataService.Batch; every row RLS-scoped to the reviewer, T5) and joins on the client, so
 * there is no per-assignment query. Drafts open through OEPFilesService.DownloadUrl (the
 * resolver signs a short-lived URL after its own permission check).
 */
@Component({
    standalone: false,
    selector: 'oep-reviews',
    templateUrl: './oep-reviews.component.html',
    styleUrl: './oep-reviews.component.scss',
})
export class OEPReviewsComponent extends OEPViewBase {
    private readonly files = inject(OEPFilesService);

    VM: ReviewsVM | null = null;
    /** Result of opening a draft, announced in the aria-live line. */
    Status: OEPStatusLine | null = null;

    protected async Load(): Promise<void> {
        const season = this.Context.Season;
        if (!season) return;
        const me = OEPDataService.G(this.Context.Me.UserID);
        const sid = OEPDataService.G(season.ID);
        const complete = season.Phase === 'Complete';
        const mine = OEPMyAssignmentsSql(me, sid);
        const pres = `SELECT PresentationID FROM FeedbackPlatform.ReviewAssignment WHERE ID IN (${mine})`;
        const link = OEPPresentationLinkSql(pres);
        const myReviews = `SELECT ID FROM FeedbackPlatform.Review WHERE ReviewAssignmentID IN (${mine})`;
        const [assignments, presentations, participants, fileRows, links, reviews, notes = []] = await this.Data.Batch([
            { EntityName: OEP_ENTITY.ReviewAssignments, ExtraFilter: `ID IN (${mine})`, Fields: ['ID', 'PresentationID'], OrderBy: 'AssignedDate, ID' },
            { EntityName: OEP_ENTITY.Presentations, ExtraFilter: `ID IN (${pres})`, Fields: ['ID', 'Title', 'Section', 'Format', 'PresentationCode', 'PresenterUserID'] },
            // The presenters' rows (career stage) — readable by their assigned reviewers (T5).
            { EntityName: OEP_ENTITY.Participants, ExtraFilter: `SeasonID='${sid}' AND UserID IN (SELECT PresenterUserID FROM FeedbackPlatform.Presentation WHERE ID IN (${pres}))`, Fields: ['UserID', 'CareerStage'] },
            { EntityName: OEP_ENTITY.Files, ExtraFilter: OEPUploadedFilesSql(pres, ['Draft']), Fields: ['ID', 'Name', 'Category', 'Status', 'ContentType', '__mj_CreatedAt'], OrderBy: '__mj_CreatedAt DESC' },
            { EntityName: OEP_ENTITY.FileEntityRecordLinks, ExtraFilter: link, Fields: ['FileID', 'RecordID'] },
            { EntityName: OEP_ENTITY.Reviews, ExtraFilter: `ReviewAssignmentID IN (${mine})`, Fields: ['ID', 'ReviewAssignmentID', 'Status', 'CompletedDate'] },
            // "One of your presenters wrote back" (markup.html:512) — asked only when the wrap callout shows.
            ...(complete ? [{ EntityName: OEP_ENTITY.ReviewThankYouNotes, ExtraFilter: `ReviewID IN (${myReviews})`, Fields: ['ReviewID'] }] : []),
        ]);

        const presByID = new Map(presentations.map((p) => [Up(Str(p, 'ID')), p]));
        const stageOf = new Map(participants.map((x) => [Up(Str(x, 'UserID')), StrOrNull(x, 'CareerStage')]));
        const drafts = OEPLatestDrafts(fileRows, links);
        const reviewOf = new Map(reviews.map((r) => [Up(Str(r, 'ReviewAssignmentID')), r]));

        const items: OEPAssignmentInput[] = assignments.flatMap((a) => {
            const p = presByID.get(Up(Str(a, 'PresentationID')));
            if (!p) return [];
            const r = reviewOf.get(Up(Str(a, 'ID')));
            const status = r ? AsReviewStatus(StrOrNull(r, 'Status')) : null;
            return [{
                AssignmentID: Str(a, 'ID'),
                Title: Str(p, 'Title'),
                SectionName: Str(p, 'Section'),
                Format: StrOrNull(p, 'Format'),
                Code: StrOrNull(p, 'PresentationCode'),
                CareerStage: stageOf.get(Up(Str(p, 'PresenterUserID'))) ?? null,
                Draft: drafts.get(Up(Str(p, 'ID'))) ?? null,
                Review: r && status ? { Status: status, CompletedDate: DateOrNull(r, 'CompletedDate') } : null,
            }];
        });

        this.VM = BuildReviewsVM({
            Phase: season.Phase, Timeline: season.Timeline, Now: new Date(),
            Assignments: items,
            Resources: this.Context.Resources.reviewer ?? [],
            ReceivedThanks: notes.length > 0,
        });
    }

    Open(row: OEPAssignmentRowVM): void {
        if (row.CtaEnabled) this.Go('review', row.AssignmentID);
    }

    async Download(row: OEPAssignmentRowVM): Promise<void> {
        if (!row.Draft) return;
        this.Status = null;
        const fileID = row.Draft.FileID;
        try {
            const tab = await OEPOpenInNewTab(() => this.files.DownloadUrl(fileID));
            if (tab.Blocked) this.Status = { Text: OEP_TAB_BLOCKED, Tone: 'error', Link: { Label: 'Open the draft', Url: tab.Url } };
        } catch (err) {
            this.Status = { Text: err instanceof Error ? err.message : String(err), Tone: 'error' };
        }
        this.Changed();
    }
}
